import React, { useEffect, useRef, useState } from "react";
import { Chart, registerables } from "chart.js";
import { Form, Select, Button, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getStates } from "../features/statecity/statecitySlice";
import { toast } from "react-toastify";
import {
  getPerformance,
  updatePerformanceStatus,
} from "../features/weeklyReview/performanceSlice";

Chart.register(...registerables);

const WeeklyReview = () => {
  const chartInstanceRef = useRef(null);
  const [form] = Form.useForm();
  const [selectedState, setSelectedState] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [chartInstance, setChartInstance] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(
    new Date().toLocaleTimeString()
  );
  const [selectedVC, setSelectedVC] = useState(null);

  const dispatch = useDispatch();
  const {
    performances,
    isLoading,
    isError,
    message: errorMessage,
  } = useSelector((state) => state.performance);

  const defaultData = [
    {
      createdAt: "2025-02-20T14:40:28.812Z",
      description: "Summary of guest lectures conducted in the last week.",
      status: "pending",
      title: "Weekly Guest Lecture Review",
      updatedAt: "2025-02-20T14:40:28.812Z",
      vcUser_id: { name: "VC1", state: "State1" },
      week: "W 4",
      _id: "67b73edc5f00ebbca33e00cf",
    },
  ];

  useEffect(() => {
    dispatch(getStates());
    setLoading(true);
    dispatch(getPerformance())
      .then((response) => {
        const data = response.payload || defaultData;
        if (data.length > 0) {
          const firstVC = data[0]?.vcUser_id?.name;
          setSelectedVC(firstVC);
          renderChart(data, firstVC);
        } else {
          message.warning("No performance data available.");
        }
      })
      .catch((error) => {
        console.error("Error fetching performance data:", error);
        message.error("Failed to fetch performance data. Using default data.");
        renderChart(defaultData, defaultData[0]?.vcUser_id?.name);
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (performances.length > 0 && selectedVC) {
      renderChart(performances, selectedVC);
    }
  }, [performances, selectedVC]);

  useEffect(() => {
    return () => {
      if (chartInstance) chartInstance.destroy();
    };
  }, [chartInstance]);

  const statelistState = useSelector(
    (state) => state.statecity.statelist || []
  );

  const resetFilters = () => {
    form.resetFields();
    setSelectedState("");
    setSelectedMonth("");
    setLoading(true);
    dispatch(getPerformance())
      .then((response) => {
        const data = response.payload || defaultData;
        if (data.length > 0) {
          const firstVC = data[0]?.vcUser_id?.name;
          setSelectedVC(firstVC);
          renderChart(data, firstVC);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleStateChange = (value) => setSelectedState(value);
  const handleMonthChange = (value) => setSelectedMonth(value);

  const handleSubmit = () => {
    setLoading(true);
    const filters = {};
    if (selectedState) filters.state = selectedState;
    if (selectedMonth) filters.month = selectedMonth;

    dispatch(getPerformance(filters))
      .then((response) => {
        const data = response.payload || defaultData;
        if (data.length > 0) {
          const firstVC = data[0]?.vcUser_id?.name;
          setSelectedVC(firstVC);
          renderChart(data, firstVC);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleStatusChange = (id, newStatus) => {
    dispatch(updatePerformanceStatus({ id, status: newStatus }))
      .then((response) => {
        toast.success("Status updated successfully!");
        // Chart will re-render via useEffect on performances change
      })
      .catch((error) => {
        console.error("Error updating status:", error);
        toast.error(
          `Failed to update status: ${error.message || "Unknown error"}`
        );
      });
  };

  const renderChart = (data, vcName) => {
    const ctx = chartInstanceRef.current?.getContext("2d");
    if (!ctx) return;

    if (chartInstance) chartInstance.destroy();

    const vcData = data.filter((perf) => perf.vcUser_id?.name === vcName);
    if (!vcData.length) return;

    const weeks = ["W 1", "W 2", "W 3", "W 4"];
    const statuses = weeks.map((week) => {
      const record = vcData.find((perf) => perf.week === week);
      return record ? record.status : "Not Reviewed";
    });

    const colors = vcData.map((status) =>
      status.status === "completed"
        ? "rgba(0, 255, 0, 0.6)"
        : "rgba(255, 0, 0, 0.6)"
    );

    const chartData = {
      labels: weeks.map((week, index) => `${week}: ${statuses[index]}`),
      datasets: [
        {
          label: `Status for ${vcName} (${
            vcData[0]?.vcUser_id?.state || "N/A"
          })`,
          data: new Array(weeks.length).fill(1),
          backgroundColor: colors,
          borderWidth: 1,
        },
      ],
    };

    const newChartInstance = new Chart(ctx, {
      type: "doughnut",
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: true, position: "right" } },
      },
    });

    setChartInstance(newChartInstance);
  };

  const styles = {
    th: { padding: "10px", backgroundColor: "#f4f4f4" },
    td: { padding: "10px" },
    tableContainer: { maxHeight: "500px", overflowY: "auto" },
  };

  return (
    <div style={{ padding: "20px" }}>
      <h4>Weekly Review Process</h4>
      <p>Last Updated: {lastUpdated}</p>
      <Form form={form} layout="inline" onFinish={handleSubmit}>
        <Form.Item name="state_name">
          <Select
            placeholder="Select State"
            onChange={handleStateChange}
            style={{ width: 150 }}
            value={selectedState}
            allowClear
          >
            {statelistState.map((i, j) => (
              <Select.Option key={j} value={i.code}>
                {i.state_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="month">
          <Select
            placeholder="Select Month"
            onChange={handleMonthChange}
            style={{ width: 150 }}
            value={selectedMonth}
            allowClear
          >
            {[
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].map((month, index) => (
              <Select.Option key={index} value={month}>
                {month}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Apply Filter
          </Button>
        </Form.Item>
        <Form.Item>
          <Button onClick={resetFilters}>Reset Filter</Button>
        </Form.Item>
        <Form.Item>
          <Select
            placeholder="Select VC"
            onChange={(value) => setSelectedVC(value)}
            style={{ width: 150 }}
            value={selectedVC}
            allowClear
          >
            {[...new Set(performances.map((perf) => perf.vcUser_id?.name))].map(
              (vcName, index) => (
                <Select.Option key={index} value={vcName}>
                  {vcName || "N/A"}
                </Select.Option>
              )
            )}
          </Select>
        </Form.Item>
      </Form>

      <div style={{ marginTop: "20px", height: "400px" }}>
        {isLoading ? (
          <p>Loading Chart...</p>
        ) : performances.length > 0 ? (
          <canvas ref={chartInstanceRef} height="300"></canvas>
        ) : (
          <p>No Data Available</p>
        )}
      </div>

      <div
        className="table-container"
        style={{ marginTop: "20px", ...styles.tableContainer }}
      >
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <th style={styles.th}>State</th>
              <th style={styles.th}>VC Name</th>
              <th style={styles.th}>Week</th>
              <th style={styles.th}>Review Status</th>
              <th style={styles.th}>Review Date</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="5">Loading...</td>
              </tr>
            ) : performances.length > 0 ? (
              performances.map((perf) => (
                <tr key={perf._id}>
                  <td style={styles.td}>{perf.vcUser_id?.state || "N/A"}</td>
                  <td style={styles.td}>{perf.vcUser_id?.name || "No VC"}</td>
                  <td style={styles.td}>{perf.week || "N/A"}</td>
                  <td style={styles.td}>
                    <select
                      value={perf.status || ""}
                      onChange={(e) =>
                        handleStatusChange(perf._id, e.target.value)
                      }
                      style={{ padding: "5px" }}
                    >
                      <option value="">{perf.status}</option>
                      <option value="pending">pending</option>
                      <option value="completed">completed</option>
                      <option value="failed">failed</option>
                    </select>
                  </td>
                  <td style={styles.td}>
                    {perf.updatedAt
                      ? new Date(perf.updatedAt).toLocaleDateString()
                      : "N/A"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">No Data Available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeeklyReview;
